import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Alert,
  Button,
  Spinner,
  Table,
  Form,
  Modal
} from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fires, auth } from '../firebase';
import Papa from 'papaparse';
import moment from 'moment';
import { FaFileUpload, FaCheck, FaTimes } from 'react-icons/fa';

export default function BulkUploadEvent() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [csvData, setCSVData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Required fields for CSV
  const requiredFields = [
    'title',
    'description',
    'address',
    'startDate',
    'endDate',
    'linkURL',
    'photoURL',
    'price'
  ];

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setIsAdmin(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Error verifying admin access');
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  const validateCSVData = (data) => {
    const errors = [];
    
    data.forEach((row, index) => {
      const rowErrors = [];
      
      // Check required fields except dates and linkURL
      requiredFields
        .filter(field => !['startDate', 'endDate', 'linkURL', 'price'].includes(field))
        .forEach(field => {
          if (!row[field]) {
            rowErrors.push(`Missing ${field}`);
          }
        });

      // Validate linkURL and price combination
      if (!row.linkURL && !row.price) {
        rowErrors.push('Price is required when linkURL is empty');
      }

      // Validate price if provided
      if (row.price) {
        const price = parseFloat(row.price);
        if (isNaN(price) || price < 0) {
          rowErrors.push('Price must be a valid non-negative number');
        }
      }

      // Validate dates if provided
      if (row.startDate || row.endDate) {
        const startDate = row.startDate ? moment(row.startDate, 'YYYY-MM-DD') : null;
        const endDate = row.endDate ? moment(row.endDate, 'YYYY-MM-DD') : null;

        if (row.startDate && !startDate.isValid()) {
          rowErrors.push('Invalid startDate format (use YYYY-MM-DD)');
        }
        if (row.endDate && !endDate.isValid()) {
          rowErrors.push('Invalid endDate format (use YYYY-MM-DD)');
        }
        if (startDate && endDate && startDate.isValid() && endDate.isValid() && endDate.isBefore(startDate)) {
          rowErrors.push('endDate must be after startDate');
        }
      }

      // Validate URL if provided
      if (row.linkURL) {
        try {
          new URL(row.linkURL);
        } catch (e) {
          rowErrors.push('Invalid linkURL format');
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: index + 2, // +2 because index starts at 0 and we need to account for header row
          errors: rowErrors
        });
      }
    });

    return errors;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setCSVData(results.data);
        const errors = validateCSVData(results.data);
        setValidationErrors(errors);
        if (errors.length === 0) {
          setShowPreview(true);
        }
      },
      error: (error) => {
        setError('Error parsing CSV file: ' + error.message);
      }
    });
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      // Get the current user's ID token
      const idToken = await auth.currentUser.getIdToken(true);

      // Prepare the data as JSON
      const requestData = {
        events: csvData
      };

      // Make API request
      const response = await fetch('https://v2adminbulkuploadevents-650318705789.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.text();
          errorMessage = `Upload failed (${response.status}): ${errorData}`;
        } catch (e) {
          errorMessage = `Upload failed with status: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      let result;
      try {
        result = await response.json();
        setSuccess(result.message || `Successfully uploaded ${csvData.length} events`);
      } catch (jsonError) {
        // If response is not JSON, still consider it a success if status was ok
        setSuccess(`Successfully uploaded ${csvData.length} events`);
      }
      
      setCSVData([]);
      setShowPreview(false);
      // Reset file input
      document.getElementById('csvFileInput').value = '';
    } catch (err) {
      console.error('Error uploading events:', err);
      if (err.message.includes('Failed to fetch')) {
        setError('Network error: Please check your internet connection and try again. If the problem persists, the server might be temporarily unavailable.');
      } else if (err.message.includes('NetworkError')) {
        setError('CORS error: The server is not accepting requests from this origin. Please contact support.');
      } else {
        setError(err.message);
      }
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card className="shadow-sm">
          <Card.Body>
            <div className="text-center">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Loading...</span>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Card className="shadow-sm">
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Unauthorized Access</Alert.Heading>
              <p>
                You do not have permission to view this page. Please contact your administrator if you believe this is a mistake.
              </p>
              <hr />
              <div className="d-flex justify-content-end">
                <Button variant="outline-danger" onClick={() => navigate('/')}>
                  Return to Home
                </Button>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Bulk Upload Events</h2>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                const csvContent = `title,description,address,startDate,endDate,linkURL,photoURL,price
Example Event,Event Description,123 Main St,2024-03-20,2024-03-21,https://example.com,https://example.com/event-photo.jpg,29.99`;
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'event_template.csv';
                a.click();
                window.URL.revokeObjectURL(url);
              }}
            >
              Download Template
            </Button>
          </div>

          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
              {success}
            </Alert>
          )}

          <Form.Group controlId="csvFileInput" className="mb-4">
            <Form.Label>Upload CSV File</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              File must be a CSV with headers: title, description, address, startDate (YYYY-MM-DD), endDate (YYYY-MM-DD), linkURL (optional), photoURL, price (required if linkURL is empty)
            </Form.Text>
          </Form.Group>

          {validationErrors.length > 0 && (
            <Alert variant="warning" className="mb-4">
              <Alert.Heading>Validation Errors</Alert.Heading>
              <ul className="mb-0">
                {validationErrors.map((error, index) => (
                  <li key={index}>
                    Row {error.row}:
                    <ul>
                      {error.errors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <Modal
            show={showPreview}
            onHide={() => setShowPreview(false)}
            size="lg"
            scrollable
          >
            <Modal.Header closeButton>
              <Modal.Title>Preview Events</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      {requiredFields.map(field => (
                        <th key={field}>{field}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.map((row, index) => (
                      <tr key={index}>
                        {requiredFields.map(field => (
                          <td key={field}>{row[field]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowPreview(false)}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={uploading || validationErrors.length > 0}
              >
                {uploading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaFileUpload className="me-2" />
                    Upload {csvData.length} Events
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          {csvData.length > 0 && !showPreview && (
            <div className="text-end">
              <Button
                variant="primary"
                onClick={() => setShowPreview(true)}
                disabled={validationErrors.length > 0}
              >
                Preview {csvData.length} Events
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
} 